import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider } from 'antd';

import { FormattedMessage } from 'react-intl';
import Pficon from 'components/Pficon';

import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';
import { hasFieldError } from 'containers/TemplatePreviewPage/utils';
import classNames from 'classnames';

import { FIELD_TYPE } from 'containers/TemplatePreviewPage/constants';
import QuestionHeader from '../QuestionHeader';
import SimpleText from '../SimpleText';

const SimpleTextRepeater = ({
  question,
  onUpdateVariable,
  questionIndex,
  skipTopMargin,
  addButtonText,
}) => {
  const {
    value: values = [],
    hasError,
    isPristine,
    placeholder,
    min: minAnswers = 0,
    max: maxAnswers = 100,
  } = question;

  const [initialQuestionSize, setInitialQuestionSize] = useState();

  useEffect(() => {
    if (!initialQuestionSize && values.length > 0) {
      setInitialQuestionSize(values.length);
    }
  }, [values, initialQuestionSize]);

  const underMinCount = Math.max(minAnswers - values.length, 0);
  const overMaxCount = Math.max(values.length - maxAnswers, 0);

  const missingMessage =
    underMinCount === 1
      ? messages.validation.fieldIncomplete
      : messages.validation.fieldIncompletePlural;

  const extraValuesMessage =
    overMaxCount === 1
      ? messages.validation.fieldHasExtra
      : messages.validation.fieldHasExtraPlural;

  const onUpdate = useCallback(
    (value, updateIndex) => {
      const updatedValues = values.map((existingValue, index) => {
        if (index === updateIndex) {
          return { text: value };
        }
        return existingValue;
      });
      onUpdateVariable(updatedValues);
    },
    [onUpdateVariable, values],
  );

  const onUpdateSingleValue = useCallback(
    (index) => (value) => {
      onUpdate(value, index);
    },
    [onUpdate],
  );

  const onAddValue = useCallback(() => {
    onUpdateVariable([
      ...values,
      { text: '' },
      // getDefaultVariableValue({ fieldType: FIELD_TYPE.TEXT }), text does not give an object back and we need an object for this case
    ]);
  }, [onUpdateVariable, values]);

  const onRemoveValue = useCallback(
    (indexToRemove) => {
      onUpdateVariable(values.filter((_, index) => index !== indexToRemove));
    },
    [onUpdateVariable, values],
  );
  const addDisabled = values.length >= maxAnswers;

  return (
    <React.Fragment>
      <QuestionHeader
        title={question.body}
        note={question.note}
        description={question.description}
        skipTopMargin={questionIndex === 0 && skipTopMargin}
      />
      <ul className="flex flex-col margin-none padding-none">
        {values.map((value = {}, index) => {
          const subQuestion = {
            body: null,
            fieldType: FIELD_TYPE.TEXT,
            value: typeof value === 'string' ? value : value.text,
            placeholder,
            required: true,
            isPristine,
          };
          const subQuestionHasError = hasFieldError(subQuestion);
          const deleteDisabled = values.length <= minAnswers;
          Object.assign(subQuestion, { hasError: subQuestionHasError });
          return (
            // eslint-disable-next-line react/no-array-index-key
            <li className="flex flex-items-center margin-btm-sm" key={index}>
              <SimpleText
                question={subQuestion}
                onUpdateVariable={onUpdateSingleValue(index)}
                hideHeader
                hideErrors
                onEnter={onAddValue}
                shouldAutoFocus={
                  initialQuestionSize && values.length > initialQuestionSize
                }
                isRepeating
              />
              <Divider
                type="vertical"
                className="vertical-margin-none horizontal-margin-xs background-grey-5"
                style={{ height: 42 }}
              />
              <Button
                disabled={deleteDisabled}
                type="button"
                onClick={() => onRemoveValue(index)}
                style={{ height: 38, lineHeight: '38px' }}
                className={classNames(
                  'outline-blue',
                  'color-grey-10',
                  'text-align-center',
                  'padding-vertical-none',
                  'padding-horizontal-xs',
                  'border-none',
                  'background-transparent',
                  'background-transparent-hover',
                  'shadow-none',
                  {
                    'color-grey-6': deleteDisabled,
                    'color-grey-6-hover': deleteDisabled,
                    'color-grey-10': !deleteDisabled,
                  },
                )}
              >
                <Pficon type="close" className="text-size-h3" />
              </Button>
            </li>
          );
        })}
        {!isPristine && hasError && Boolean(underMinCount) && (
          <li className="display-block margin-btm-sm">
            <span className="color-red-6" key={`${question.id}-validation`}>
              <FormattedMessage
                {...missingMessage}
                values={{
                  missingValuesCount: underMinCount,
                }}
              />
            </span>
          </li>
        )}
        {!isPristine && hasError && Boolean(overMaxCount) && (
          <li className="display-block margin-btm-sm">
            <span
              className="color-red-6"
              key={`${question.id}-validation-count`}
            >
              <FormattedMessage
                {...extraValuesMessage}
                values={{
                  extraValuesCount: overMaxCount,
                }}
              />
            </span>
          </li>
        )}
        {!isPristine && hasError && !(underMinCount || overMaxCount) && (
          <li className="display-block margin-vertical-sm">
            <span className="color-red-6">
              <FormattedMessage {...messages.validation.fieldHasSubError} />
            </span>
          </li>
        )}
        <li className="display-block">
          <Button
            type="button"
            onClick={onAddValue}
            disabled={addDisabled}
            style={{ height: 'auto' }}
            className={classNames(
              'outline-blue',
              'color-grey-1',
              'border-none',
              'text-size-secondary',
              'padding-horizontal-xs',
              'padding-vertical-xxs',
              'shadow-none',
              {
                'background-grey-8': addDisabled,
                'background-grey-8-hover': addDisabled,
                'background-grey-10': !addDisabled,
              },
            )}
          >
            {addButtonText || 'Add another'}
          </Button>
        </li>
      </ul>
    </React.Fragment>
  );
};

SimpleTextRepeater.propTypes = {
  question: PropTypes.object,
  questionIndex: PropTypes.number,
  onUpdateVariable: PropTypes.func.isRequired,
};

export default SimpleTextRepeater;
